import React, { useRef } from "react";
import { ArrowRight, Check, Crown } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useInView } from "../hooks/useInView";
import { services, images } from "../data/mock";

const imageMap = {
  wedding: images.wedding,
  corporate: images.corporate,
  private: images.private,
};

export const ServicesSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.1 });

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="services"
      ref={sectionRef}
      className="section-padding bg-warm-white relative"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div
          className={`text-center max-w-2xl mx-auto mb-16 ${
            isInView ? "animate-fade-in-up" : "opacity-0"
          }`}
        >
          <span className="text-gold text-sm font-semibold tracking-widest uppercase mb-4 block">
            Our Services
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-charcoal mb-6">
            Tailored to Your{" "}
            <span className="text-gold-gradient">Celebration</span>
          </h2>
          <p className="text-charcoal/60 text-lg leading-relaxed">
            From intimate wedding receptions to grand corporate galas, we bring
            the expertise and passion to make every event extraordinary.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className={`service-card group rounded-2xl overflow-hidden bg-white border ${
                service.isPrimary
                  ? "border-gold/40 shadow-lg shadow-gold/10 lg:scale-105"
                  : "border-border"
              } ${isInView ? "animate-fade-in-up" : "opacity-0"}`}
              style={{
                animationDelay: `${(index + 1) * 200}ms`,
                animationFillMode: "both",
              }}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={imageMap[service.image]}
                  alt={service.title}
                  className="w-full h-full object-cover"
                  style={{ transition: "transform 0.7s ease" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                {service.badge && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-gold text-white border-0 font-medium px-3 py-1 flex items-center gap-1">
                      <Crown className="h-3 w-3" />
                      {service.badge}
                    </Badge>
                  </div>
                )}
                <h3 className="absolute bottom-4 left-6 font-display text-2xl font-bold text-white">
                  {service.title}
                </h3>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-charcoal/60 leading-relaxed mb-6 text-sm">
                  {service.description}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, fIndex) => (
                    <li
                      key={fIndex}
                      className="flex items-center gap-3 text-sm text-charcoal/70"
                    >
                      <Check className="h-4 w-4 text-gold flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={scrollToContact}
                  variant={service.isPrimary ? "default" : "outline"}
                  className={`w-full rounded-full font-medium ${
                    service.isPrimary
                      ? "bg-gold hover:bg-gold-dark text-white"
                      : "border-gold/30 text-gold hover:bg-gold/5"
                  }`}
                >
                  Inquire Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
